import {
  APP_TIMEZONE,
  atualizarCustoSchema,
  formatBRL,
  fromReais,
  frota,
  lancarCustoSchema,
  localDayKey,
  ratearCustoAnual,
  ROTULO_DO_TIPO_DE_CUSTO,
  TAMANHO_MAXIMO_DE_PAGINA,
  type TipoDeCustoDeVeiculo,
  TIPOS_DE_CUSTO_LANCAVEIS,
  TIPOS_RATEAVEIS,
} from '@locaveri/shared'
import { ExternalLink, LoaderCircle, RefreshCw } from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { errorMessage } from '@/lib/api'
import { formatarData } from '@/lib/datas'
import { deCampoDeReaisParaWire, formatarMoedaDoWire, paraCampoDeReais } from '@/lib/dinheiro'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'

import {
  type Custo,
  type FiltrosDeVeiculos,
  identificacaoDoVeiculo,
  rotuloDaCompetencia,
  useAtualizarCusto,
  useLancarCusto,
  useVeiculos,
} from './api'
import { BadgeDeOrigemDeCusto } from './badges'

/**
 * Lançamento e correção de custo de frota.
 *
 * ---------------------------------------------------------------------------
 * SÓ SE LANÇA O QUE SAIU DO CAIXA
 * ---------------------------------------------------------------------------
 * O seletor de tipo usa `TIPOS_DE_CUSTO_LANCAVEIS`, não a lista inteira:
 * depreciação, custo de capital e overhead NÃO têm contrapartida em caixa —
 * ninguém pagou boleto de depreciação. Eles nascem do fechamento mensal, e a
 * API os recusa aqui (o CHECK do banco também). Oferecer a opção seria oferecer
 * um caminho que termina em 422.
 *
 * Pelo mesmo motivo, linha de origem `CALCULO_AUTOMATICO` não se edita: ela é o
 * resultado de um cálculo reproduzível, e corrigi-la à mão criaria divergência
 * silenciosa com o que o motor recalcula. A tela de custos nem oferece o botão;
 * a guarda daqui é a segunda linha de defesa.
 *
 * ---------------------------------------------------------------------------
 * RATEIO: POR QUE ELE EXISTE
 * ---------------------------------------------------------------------------
 * O IPVA é pago inteiro em janeiro e vale o ano todo. Sem rateio, janeiro
 * parece prejuízo e fevereiro parece lucro — e a rentabilidade por veículo, que
 * é o motivo de este módulo existir, vira ruído de calendário. Com
 * `ratearEmMeses > 1` a API grava o lançamento de CAIXA no mês do pagamento e,
 * na mesma transação, as parcelas de COMPETÊNCIA apontando para ele.
 *
 * A prévia usa `ratearCustoAnual` do `@locaveri/shared` — a MESMA função que a
 * API chama. Reimplementar a divisão aqui daria um número que "quase" bate, e o
 * "quase" apareceria como um centavo de diferença no relatório.
 *
 * ---------------------------------------------------------------------------
 * COMPROVANTE: LINK, NÃO ARQUIVO
 * ---------------------------------------------------------------------------
 * O contrato da API é `anexoUrl` — uma URL. Não existe endpoint de upload nesta
 * fase, e o cliente HTTP do painel serializa TODO corpo com `JSON.stringify`:
 * um `FormData` viraria `{}` em silêncio, sem erro e sem arquivo. Então o campo
 * guarda o endereço do comprovante onde ele já está hospedado. Quando a Fase 2
 * ganhar o upload para o S3/MinIO, este campo passa a ser preenchido pelo
 * retorno do upload — e nada mais muda.
 */

const CAMPOS = [
  'vehicleId',
  'tipo',
  'valor',
  'data',
  'ratearEmMeses',
  'fornecedor',
  'notaFiscal',
  'hodometro',
  'anexoUrl',
  'observacoes',
] as const

const MESES_POR_ANO = 12

/**
 * A frota inteira, baixados inclusive.
 *
 * Multa e acerto de IPVA chegam DEPOIS da baixa; esconder o veículo baixado
 * aqui deixaria esse custo sem onde ser lançado. O objeto é constante de módulo
 * para a chave de cache ser estável — e é a mesma da tela de custos, então as
 * duas dividem uma requisição só.
 */
const TODOS_OS_VEICULOS: FiltrosDeVeiculos = {
  pagina: 1,
  tamanho: TAMANHO_MAXIMO_DE_PAGINA,
  busca: '',
  categoryId: '',
  locationId: '',
  status: '',
  blindagemVencendoEmDias: '',
  incluirBaixados: true,
}

const TIPO_PADRAO: TipoDeCustoDeVeiculo = TIPOS_DE_CUSTO_LANCAVEIS[0] ?? 'OUTRO'

interface FormularioDeCusto {
  vehicleId: string
  tipo: TipoDeCustoDeVeiculo
  /** Reais digitados (`"1.250,00"`), convertidos para centavos no envio. */
  valor: string
  /** `AAAA-MM-DD`. A competência é o MÊS desta data. */
  data: string
  ratearEmMeses: string
  fornecedor: string
  notaFiscal: string
  hodometro: string
  anexoUrl: string
  observacoes: string
}

/**
 * Hoje em `AAAA-MM-DD`, no fuso da OPERAÇÃO.
 *
 * `toISOString().slice(0, 10)` daria o dia UTC — em São Paulo, das 21h em
 * diante, o formulário abriria já no dia seguinte e o custo cairia na
 * competência errada em toda virada de mês. E o relógio do APARELHO também não
 * serve (invariante nº 2): quem abrisse o painel de Lisboa às 00h30 de 1º de
 * outubro lançaria em outubro um custo que em São Paulo ainda é de 30 de
 * setembro.
 */
function hoje(): string {
  return localDayKey(new Date(), APP_TIMEZONE)
}

/**
 * A competência N meses depois de uma data `AAAA-MM-DD`, em `AAAA-MM-01`.
 *
 * Aritmética de inteiros sobre o texto, sem passar por `Date`: `new Date` em
 * data pura é meia-noite UTC, e no fuso de São Paulo o mês recuaria um dia — a
 * última parcela do rateio apareceria no mês anterior ao real.
 */
function competenciaSomandoMeses(data: string, meses: number): string {
  const ano = Number(data.slice(0, 4))
  const mes = Number(data.slice(5, 7))
  const total = ano * MESES_POR_ANO + (mes - 1) + meses
  const anoFinal = Math.trunc(total / MESES_POR_ANO)
  const mesFinal = (total % MESES_POR_ANO) + 1

  return `${String(anoFinal)}-${String(mesFinal).padStart(2, '0')}-01`
}

/** `0` é hodômetro válido — por isso o teste é de ausência, não de valor falsy. */
function campoDeHodometro(hodometro: number | null | undefined): string {
  return hodometro === null || hodometro === undefined ? '' : String(hodometro)
}

function valoresIniciais(custo: Custo | undefined): FormularioDeCusto {
  return {
    vehicleId: custo?.vehicleId ?? '',
    tipo: custo?.tipo ?? TIPO_PADRAO,
    valor: paraCampoDeReais(custo?.valor),
    data: (custo?.data ?? hoje()).slice(0, 10),
    ratearEmMeses: '1',
    fornecedor: custo?.fornecedor ?? '',
    notaFiscal: custo?.notaFiscal ?? '',
    hodometro: campoDeHodometro(custo?.hodometro),
    anexoUrl: custo?.anexoUrl ?? '',
    observacoes: custo?.observacoes ?? '',
  }
}

export interface DialogoDeCustoProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `undefined` lança um custo novo; preenchido corrige os campos descritivos. */
  readonly custo?: Custo | undefined
}

export function DialogoDeCusto({ aberto, aoFechar, custo }: DialogoDeCustoProps): ReactElement {
  const editando = custo !== undefined
  const automatico = custo?.origem === 'CALCULO_AUTOMATICO'
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const veiculos = useVeiculos(TODOS_OS_VEICULOS)
  const lancar = useLancarCusto()
  const atualizar = useAtualizarCusto()

  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormularioDeCusto>({ defaultValues: valoresIniciais(undefined) })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir
  // "corrigir" depois de "lançar" mostraria os valores do formulário anterior.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      reset(valoresIniciais(custo))
    }
  }, [aberto, custo, reset])

  const tipo = watch('tipo')
  const valorDigitado = watch('valor')
  const dataDigitada = watch('data')
  const mesesDigitados = watch('ratearEmMeses')
  const anexoDigitado = watch('anexoUrl')

  const rateavel = TIPOS_RATEAVEIS.includes(tipo)
  const ocupado = lancar.isPending || atualizar.isPending

  /**
   * A prévia do rateio, pela MESMA função que a API usa. Só aparece quando os
   * três ingredientes existem — uma prévia com número inventado seria pior do
   * que prévia nenhuma.
   */
  const previaDoRateio = useMemo(() => {
    const meses = Number(mesesDigitados)

    if (!rateavel || !Number.isInteger(meses) || meses < 2 || dataDigitada.length < 7) {
      return null
    }

    // `fromReais` LANÇA em valor malformado, e enquanto a pessoa digita ele é
    // malformado na maior parte do tempo — a prévia some, o formulário não cai.
    try {
      const parcelas = ratearCustoAnual(fromReais(valorDigitado), meses)
      const primeira = parcelas[0]

      if (primeira === undefined) {
        return null
      }

      return {
        meses,
        primeira: formatBRL(primeira),
        primeiraCompetencia: rotuloDaCompetencia(competenciaSomandoMeses(dataDigitada, 0)),
        ultimaCompetencia: rotuloDaCompetencia(competenciaSomandoMeses(dataDigitada, meses - 1)),
      }
    } catch {
      return null
    }
  }, [rateavel, mesesDigitados, valorDigitado, dataDigitada])

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    if (custo !== undefined) {
      // Na correção só os campos descritivos viajam. Valor, tipo, data e veículo
      // mudariam o rateio já gerado e a competência já fechada — para isso o
      // caminho é estornar e lançar de novo.
      //
      // Campo vazio vira `null` (e não ausência): `undefined` significa "não
      // mexa", e apagar o fornecedor precisa APAGAR o fornecedor.
      const entrada = {
        fornecedor: valores.fornecedor.trim().length > 0 ? valores.fornecedor : null,
        notaFiscal: valores.notaFiscal.trim().length > 0 ? valores.notaFiscal : null,
        hodometro: valores.hodometro.trim().length > 0 ? valores.hodometro : null,
        anexoUrl: valores.anexoUrl.trim().length > 0 ? valores.anexoUrl : null,
        observacoes: valores.observacoes.trim().length > 0 ? valores.observacoes : null,
      }

      const validado = atualizarCustoSchema.safeParse(entrada)

      if (!validado.success) {
        setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
        return
      }

      // `atualizarCustoSchema` não transforma dinheiro — aqui vale `validado.data`,
      // já com o hodômetro convertido para número.
      atualizar.mutate(
        { id: custo.id, dados: validado.data },
        {
          onSuccess: () => {
            toast.success('Lançamento corrigido')
            aoFechar()
          },
          onError: (causa: unknown) => {
            setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
          },
        },
      )
      return
    }

    const valorWire = deCampoDeReaisParaWire(valores.valor)

    if (valorWire === null) {
      setError('valor', { type: 'validate', message: 'valor inválido — use o formato 1.250,00' })
      return
    }

    const entrada = {
      vehicleId: valores.vehicleId,
      tipo: valores.tipo,
      valor: valorWire,
      data: valores.data,
      // Tipo não rateável volta a 1 mesmo que o campo tenha ficado com outro
      // número de um preenchimento anterior — o schema recusaria, e recusar o
      // formulário por um campo invisível é a pior mensagem de erro possível.
      ratearEmMeses: rateavel ? valores.ratearEmMeses : '1',
      ...(valores.fornecedor.trim().length > 0 ? { fornecedor: valores.fornecedor } : {}),
      ...(valores.notaFiscal.trim().length > 0 ? { notaFiscal: valores.notaFiscal } : {}),
      ...(valores.hodometro.trim().length > 0 ? { hodometro: valores.hodometro } : {}),
      ...(valores.anexoUrl.trim().length > 0 ? { anexoUrl: valores.anexoUrl } : {}),
      ...(valores.observacoes.trim().length > 0 ? { observacoes: valores.observacoes } : {}),
    }

    const validado = lancarCustoSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    // `entrada`, e não `validado.data`: `lancarCustoSchema` transforma `valor`
    // em `bigint`, e `JSON.stringify` de um `bigint` LANÇA.
    lancar.mutate(entrada, {
      onSuccess: (criado) => {
        toast.success('Custo lançado', {
          description:
            criado.parcelas > 0
              ? `Geramos ${String(criado.parcelas)} parcelas de competência apontando para este lançamento.`
              : `Competência de ${rotuloDaCompetencia(criado.competencia)}.`,
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
      },
    })
  })

  const listaDeVeiculos = veiculos.data?.itens ?? []

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editando ? 'Corrigir lançamento' : 'Lançar custo'}</DialogTitle>
          <DialogDescription>
            {editando
              ? 'Só os campos descritivos. Valor, tipo, data e veículo não se editam — para isso, estorne e lance de novo.'
              : 'O que saiu do caixa, na competência do mês do pagamento.'}
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          {automatico ? (
            <Alert variant="warning">
              <AlertTitle>Este lançamento é do sistema</AlertTitle>
              <AlertDescription>
                Linhas de competência nascem do fechamento mensal e não se corrigem à mão. Ajuste a
                política de custo e rode o fechamento do mês seguinte.
              </AlertDescription>
            </Alert>
          ) : null}

          {custo === undefined ? null : (
            <div className="rounded-lg border border-border bg-surface p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">
                    {identificacaoDoVeiculo(custo.veiculo)}
                  </p>
                  <p className="truncate text-xs text-fg-secondary">
                    {ROTULO_DO_TIPO_DE_CUSTO[custo.tipo]} · {formatarData(custo.data)} · competência
                    de {rotuloDaCompetencia(custo.competencia)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <BadgeDeOrigemDeCusto origem={custo.origem} />
                  <span className="money-cell text-foreground">
                    {formatarMoedaDoWire(custo.valor)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {editando ? null : (
            <>
              <Campo
                rotulo="Veículo"
                obrigatorio
                erro={errors.vehicleId?.message}
                dica="Custo é sempre de um veículo — é assim que a rentabilidade sai por placa."
              >
                {(campo) => (
                  <Select
                    {...campo}
                    {...register('vehicleId', { required: 'selecione o veículo' })}
                    disabled={ocupado || veiculos.isPending}
                  >
                    <option value="">
                      {veiculos.isPending ? 'Carregando a frota…' : 'Selecione o veículo'}
                    </option>
                    {listaDeVeiculos.map((veiculo) => (
                      <option key={veiculo.id} value={veiculo.id}>
                        {identificacaoDoVeiculo(veiculo)} · {veiculo.categoria.nome}
                        {veiculo.dataDeBaixa === null ? '' : ' (baixado)'}
                      </option>
                    ))}
                  </Select>
                )}
              </Campo>

              {/* Os três estados da frota são distintos: carregando é o texto
                  da própria opção, erro oferece o "tentar de novo" (sem ele a
                  saída seria fechar e reabrir o diálogo) e frota vazia diz o
                  que fazer em vez de mostrar uma lista muda. */}
              {veiculos.isError ? (
                <Alert variant="danger">
                  <AlertTitle>Não foi possível carregar a frota</AlertTitle>
                  <AlertDescription>{errorMessage(veiculos.error)}</AlertDescription>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      void veiculos.refetch()
                    }}
                  >
                    <RefreshCw aria-hidden="true" />
                    Tentar de novo
                  </Button>
                </Alert>
              ) : !veiculos.isPending && listaDeVeiculos.length === 0 ? (
                <Alert variant="warning">
                  <AlertTitle>Nenhum veículo cadastrado</AlertTitle>
                  <AlertDescription>
                    Custo é sempre de um veículo. Cadastre a frota antes de lançar o primeiro gasto.
                  </AlertDescription>
                </Alert>
              ) : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <Campo rotulo="Tipo" obrigatorio erro={errors.tipo?.message}>
                  {(campo) => (
                    <Select {...campo} {...register('tipo')} disabled={ocupado}>
                      {TIPOS_DE_CUSTO_LANCAVEIS.map((item) => (
                        <option key={item} value={item}>
                          {ROTULO_DO_TIPO_DE_CUSTO[item]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Campo>

                <Campo
                  rotulo="Valor"
                  obrigatorio
                  erro={errors.valor?.message}
                  dica="Em reais. Ex.: 1.250,00"
                >
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('valor', {
                        required: 'informe o valor',
                        validate: (valor: string) =>
                          deCampoDeReaisParaWire(valor) !== null ||
                          'valor inválido — use o formato 1.250,00',
                      })}
                      inputMode="decimal"
                      placeholder="0,00"
                      className="tabular-money"
                      disabled={ocupado}
                    />
                  )}
                </Campo>

                <Campo
                  rotulo="Data do gasto"
                  obrigatorio
                  erro={errors.data?.message}
                  dica="A competência é o MÊS desta data. Pagou em 10/01? A competência é janeiro."
                >
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('data', { required: 'informe a data do gasto' })}
                      type="date"
                      className="tabular-money"
                      disabled={ocupado}
                    />
                  )}
                </Campo>

                {rateavel ? (
                  <Campo
                    rotulo="Ratear em"
                    erro={errors.ratearEmMeses?.message}
                    dica={`Em meses, de 1 a ${String(frota.MESES_DE_RATEIO_MAXIMO)}. 1 lança tudo no mês do pagamento.`}
                  >
                    {(campo) => (
                      <Input
                        {...campo}
                        {...register('ratearEmMeses')}
                        type="number"
                        min={1}
                        max={frota.MESES_DE_RATEIO_MAXIMO}
                        inputMode="numeric"
                        className="tabular-money"
                        disabled={ocupado}
                      />
                    )}
                  </Campo>
                ) : null}
              </div>

              {rateavel ? (
                <Alert variant="info">
                  <AlertTitle>Por que ratear</AlertTitle>
                  <AlertDescription>
                    Sem rateio, o IPVA faz janeiro parecer prejuízo e fevereiro parecer lucro. O
                    valor pago continua inteiro no caixa do mês; o rateio só distribui o que cada
                    mês CONSUMIU.
                    {previaDoRateio === null ? null : (
                      <>
                        {' '}
                        <span className="tabular-money">
                          {String(previaDoRateio.meses)} parcelas de {previaDoRateio.primeira}
                        </span>
                        , de {previaDoRateio.primeiraCompetencia} a{' '}
                        {previaDoRateio.ultimaCompetencia}. A soma das parcelas é exatamente o valor
                        pago.
                      </>
                    )}
                  </AlertDescription>
                </Alert>
              ) : null}
            </>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Fornecedor" erro={errors.fornecedor?.message}>
              {(campo) => (
                <Input
                  {...campo}
                  {...register('fornecedor')}
                  maxLength={120}
                  disabled={ocupado || automatico}
                />
              )}
            </Campo>

            <Campo rotulo="Nota fiscal" erro={errors.notaFiscal?.message}>
              {(campo) => (
                <Input
                  {...campo}
                  {...register('notaFiscal')}
                  maxLength={60}
                  className="tabular-money"
                  disabled={ocupado || automatico}
                />
              )}
            </Campo>

            <Campo
              rotulo="Hodômetro"
              erro={errors.hodometro?.message}
              dica="Km no momento do gasto. É o que liga custo a rodagem no relatório."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('hodometro')}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  className="tabular-money"
                  disabled={ocupado || automatico}
                />
              )}
            </Campo>

            <Campo
              rotulo="Link do comprovante"
              erro={errors.anexoUrl?.message}
              dica="Cole o endereço da nota, do boleto ou da foto. O painel guarda o link, não o arquivo."
              className="sm:col-span-2"
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('anexoUrl')}
                  type="url"
                  inputMode="url"
                  placeholder="https://"
                  disabled={ocupado || automatico}
                />
              )}
            </Campo>
          </div>

          {anexoDigitado.trim().length === 0 ? null : (
            <a
              href={anexoDigitado}
              target="_blank"
              rel="noreferrer noopener"
              className="text-brand inline-flex items-center gap-1.5 text-xs underline-offset-4 hover:underline"
            >
              <ExternalLink className="size-3.5" aria-hidden="true" />
              Abrir o comprovante em outra aba
            </a>
          )}

          <Campo rotulo="Observações" erro={errors.observacoes?.message}>
            {(campo) => (
              <Textarea
                {...campo}
                {...register('observacoes')}
                rows={3}
                maxLength={1000}
                disabled={ocupado || automatico}
              />
            )}
          </Campo>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              {automatico ? 'Fechar' : 'Cancelar'}
            </Button>

            {/* Linha de competência não se salva: o botão some, e não fica
                cinza — oferecer uma ação que a API recusa é oferecer um 422. */}
            {automatico ? null : (
              <Button type="submit" disabled={ocupado}>
                {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
                {editando ? 'Salvar correção' : 'Lançar custo'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
